import React, { useState } from 'react';

const PERIODS = [
  { key: 'Curr Qtr', label: 'Current Quarter' },
  { key: 'Next Qtr', label: 'Next Quarter' },
  { key: 'Curr Yr', label: 'Current Year' },
  { key: 'Next Yr', label: 'Next Year' },
];

// Expected growth between two *different* periods' current estimates.
// Unlike PERIODS above (which measure how one period's estimate moved over the
// last 90 days), these compare today's consensus for the next period against
// today's consensus for the current period - i.e. the growth analysts expect.
// Same definition as expected_qtr_growth_curr_to_next / expected_yr_growth_curr_to_next
// in ScoringBuilder/fieldCatalog.js, so the two surfaces always agree.
const GROWTH_PERIODS = [
  { key: 'qtr', label: 'Curr → Next Qtr', fromKey: 'Curr Qtr', toKey: 'Next Qtr' },
  { key: 'yr', label: 'Curr → Next Yr', fromKey: 'Curr Yr', toKey: 'Next Yr' },
];

function ResearchEPSTrendsTable({ data, onTickerClick }) {
  const [viewMode, setViewMode] = useState('heatmap');

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const calculateTrendPercent = (row, periodKey) => {
    const values = [
      row[`${periodKey} 90`],
      row[`${periodKey} 60`],
      row[`${periodKey} 30`],
      row[`${periodKey} 7`],
      row[`${periodKey} Curr`],
    ];
    const valid = values.filter(v => v != null && !isNaN(v));
    if (valid.length < 2) return null;
    const first = valid[0];
    const last = valid[valid.length - 1];
    if (first === 0) return null;
    return ((last - first) / Math.abs(first)) * 100;
  };

  // Mirrors the `pct_change` op in ScoringBuilder/scoring.js:
  //   b === 0 ? null : (a - b) / Math.abs(b)
  // scaled x100 to match calculateTrendPercent's display convention.
  // Math.abs on the denominator is deliberate: a swing from an expected loss
  // to an expected profit reads as a large positive improvement.
  const calculateGrowthPercent = (row, gp) => {
    const from = row[`${gp.fromKey} Curr`];
    const to = row[`${gp.toKey} Curr`];
    if (from == null || to == null || isNaN(from) || isNaN(to)) return null;
    if (from === 0) return null;
    return ((to - from) / Math.abs(from)) * 100;
  };

  const getTrendArrow = (change) => {
    if (change == null || Math.abs(change) < 0.1) return { arrow: '\u2192', color: 'text-gray-400' };
    if (change > 0) return { arrow: '\u2197', color: 'text-green-400' };
    return { arrow: '\u2198', color: 'text-red-400' };
  };

  const formatTrendPercent = (value) => {
    if (value == null || isNaN(value)) return '--';
    const formatted = value.toFixed(1);
    return value > 0 ? `+${formatted}%` : `${formatted}%`;
  };

  // Heatmap color logic. Each grid normalizes against its own max so the two
  // heatmaps stay readable independently: revision percentages run +/-1-9%
  // while expected-growth percentages routinely run +/-30-200%, and a shared
  // scale would wash the revision colors out to near-invisible.
  const maxAbsOf = (values) => {
    const valid = values.filter(v => v != null && !isNaN(v));
    return valid.length > 0
      ? Math.max(Math.abs(Math.min(...valid)), Math.abs(Math.max(...valid)), 5)
      : 5;
  };

  const makeColorClass = (maxAbs) => (value) => {
    if (value == null || isNaN(value)) return 'bg-gray-600';
    const intensity = Math.abs(value) / maxAbs;
    if (value > 0) {
      if (intensity < 0.2) return 'bg-green-900/40';
      if (intensity < 0.4) return 'bg-green-800/60';
      if (intensity < 0.6) return 'bg-green-700/80';
      if (intensity < 0.8) return 'bg-green-600';
      return 'bg-green-500';
    } else {
      if (intensity < 0.2) return 'bg-red-900/40';
      if (intensity < 0.4) return 'bg-red-800/60';
      if (intensity < 0.6) return 'bg-red-700/80';
      if (intensity < 0.8) return 'bg-red-600';
      return 'bg-red-500';
    }
  };

  const rows = data || [];
  const maxAbsVal = maxAbsOf(rows.flatMap(row => PERIODS.map(p => calculateTrendPercent(row, p.key))));
  const growthMaxAbsVal = maxAbsOf(rows.flatMap(row => GROWTH_PERIODS.map(gp => calculateGrowthPercent(row, gp))));

  const getColorClass = makeColorClass(maxAbsVal);
  const getGrowthColorClass = makeColorClass(growthMaxAbsVal);

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No EPS trends data available. Run an analysis from the Analysis Input tab.</p>
      </div>
    );
  }

  const tickers = data.map(row => row.Ticker).filter(Boolean);
  const rowFor = (ticker) => data.find(r => r.Ticker === ticker);

  const getTooltipContent = (ticker, period, value, rawData) => {
    if (!rawData || value == null) return `${ticker} ${period}: No data`;
    const change = rawData.current - rawData.start;
    const changeSymbol = change >= 0 ? '+' : '';
    return `${ticker} - ${period}\n90d ago: ${formatCurrency(rawData.start)}\nCurrent: ${formatCurrency(rawData.current)}\nChange: ${changeSymbol}${formatCurrency(change)} (${formatTrendPercent(value)})`;
  };

  const getGrowthTooltipContent = (ticker, gp, value, rawData) => {
    if (!rawData || value == null) return `${ticker} ${gp.label}: No data`;
    const change = rawData.to - rawData.from;
    const changeSymbol = change >= 0 ? '+' : '';
    return `${ticker} - ${gp.label}\n${gp.fromKey}: ${formatCurrency(rawData.from)}\n${gp.toKey}: ${formatCurrency(rawData.to)}\nChange: ${changeSymbol}${formatCurrency(change)} (${formatTrendPercent(value)})`;
  };

  // Build heatmap data: rows = periods, columns = tickers
  const heatmapData = PERIODS.map(period => ({
    key: period.key,
    rowLabel: `${period.key} Trend`,
    cells: tickers.map(ticker => {
      const row = rowFor(ticker);
      const value = row ? calculateTrendPercent(row, period.key) : null;
      const rawData = row
        ? { start: row[`${period.key} 90`], current: row[`${period.key} Curr`], ticker }
        : null;
      return { value, tooltip: getTooltipContent(ticker, period.key, value, rawData) };
    }),
  }));

  // Expected growth grid: rows = curr->next qtr / curr->next yr, columns = tickers
  const growthHeatmapData = GROWTH_PERIODS.map(gp => ({
    key: gp.key,
    rowLabel: gp.label,
    cells: tickers.map(ticker => {
      const row = rowFor(ticker);
      const value = row ? calculateGrowthPercent(row, gp) : null;
      const rawData = row
        ? { from: row[`${gp.fromKey} Curr`], to: row[`${gp.toKey} Curr`], ticker }
        : null;
      return { value, tooltip: getGrowthTooltipContent(ticker, gp, value, rawData) };
    }),
  }));

  const gridColumns = { gridTemplateColumns: `120px repeat(${tickers.length}, 80px)` };

  const renderGrid = (gridRows, colorFn) => (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        {/* Header with tickers */}
        <div className="grid gap-px mb-1" style={gridColumns}>
          <div className="bg-gray-700 p-2 text-sm font-medium text-gray-300 text-center">
            Period
          </div>
          {tickers.map((ticker) => (
            <div key={ticker} className="bg-gray-700 p-2 text-xs font-medium text-center">
              <div
                className="text-blue-400 hover:text-blue-300 hover:underline cursor-pointer"
                onClick={() => onTickerClick && onTickerClick(ticker)}
              >
                {ticker}
              </div>
            </div>
          ))}
        </div>

        {/* Heatmap rows */}
        {gridRows.map(({ key, rowLabel, cells }) => (
          <div key={key} className="grid gap-px mb-1" style={gridColumns}>
            <div className="bg-gray-700 p-2 text-sm text-gray-300 flex items-center">
              <span className="font-medium">{rowLabel}</span>
            </div>
            {cells.map(({ value, tooltip }, index) => {
              const trendInfo = getTrendArrow(value);
              return (
                <div
                  key={index}
                  className={`p-2 text-xs text-center transition-all hover:scale-105 hover:z-10 relative cursor-help ${colorFn(value)}`}
                  title={tooltip}
                >
                  <div className="text-white font-mono text-xs leading-tight">
                    {formatTrendPercent(value)}
                  </div>
                  <div className={`text-xs mt-1 ${trendInfo.color}`}>
                    {value != null && !isNaN(value) ? trendInfo.arrow : '?'}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  const renderLegend = () => (
    <div className="mt-4 flex items-center justify-center space-x-6 text-xs text-gray-400">
      <div className="flex items-center space-x-2">
        <div className="w-3 h-3 bg-red-500 rounded"></div>
        <span>Strong Decline</span>
      </div>
      <div className="flex items-center space-x-2">
        <div className="w-3 h-3 bg-red-800 rounded"></div>
        <span>Decline</span>
      </div>
      <div className="flex items-center space-x-2">
        <div className="w-3 h-3 bg-gray-600 rounded"></div>
        <span>Neutral</span>
      </div>
      <div className="flex items-center space-x-2">
        <div className="w-3 h-3 bg-green-800 rounded"></div>
        <span>Improvement</span>
      </div>
      <div className="flex items-center space-x-2">
        <div className="w-3 h-3 bg-green-500 rounded"></div>
        <span>Strong Improvement</span>
      </div>
    </div>
  );

  const renderHeatmapView = () => (
    <div className="space-y-4">
      {/* Estimate revisions over the last 90 days */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white mb-2">EPS Trend Slopes (90d → Current)</h3>
          <p className="text-sm text-gray-400">
            Heatmap showing the slope of EPS estimate changes over time.
            <span className="text-green-400 ml-2">Green = Improving estimates</span>
            <span className="text-red-400 ml-2">Red = Declining estimates</span>
          </p>
        </div>

        {renderGrid(heatmapData, getColorClass)}
        {renderLegend()}
      </div>

      {/* Expected growth between periods (snapshot, not a revision over time) */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white mb-2">Expected EPS Growth (Current → Next)</h3>
          <p className="text-sm text-gray-400">
            Current consensus for the next period vs. current consensus for this period — the growth
            analysts expect right now, not a revision over time.
            <span className="text-green-400 ml-2">Green = Growth expected</span>
            <span className="text-red-400 ml-2">Red = Decline expected</span>
          </p>
        </div>

        {renderGrid(growthHeatmapData, getGrowthColorClass)}
        {renderLegend()}
      </div>
    </div>
  );

  const renderDetailedView = () => (
    <div className="space-y-6">
      {data.map((row, stockIndex) => (
        <div key={stockIndex} className="bg-gray-800 rounded-lg p-6">
          {/* Stock Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-white">
              <span
                className="cursor-pointer text-blue-400 hover:text-blue-300 hover:underline"
                onClick={() => onTickerClick && onTickerClick(row.Ticker)}
              >
                {row.Ticker}
              </span>
              {' '}- EPS Estimate Timeline
            </h3>
          </div>

          {/* Timeline Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-600 rounded-lg">
              <thead>
                <tr style={{ backgroundColor: '#1a1a1a' }}>
                  <th className="text-left py-3 px-4 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>Period</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>90d Ago</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>60d Ago</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>30d Ago</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>7d Ago</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>Current</th>
                  <th className="text-center py-3 px-4 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {PERIODS.map((period, periodIndex) => {
                  const percentChange = calculateTrendPercent(row, period.key);
                  const trend = getTrendArrow(percentChange);

                  return (
                    <tr key={period.key} className={`border-t border-gray-600 ${periodIndex % 2 === 0 ? 'bg-gray-800/50' : ''} hover:bg-[rgba(33,138,255,0.15)] transition-colors`}>
                      <td className="py-3 px-4 font-medium text-white">
                        {period.label}
                      </td>
                      <td className="py-3 px-3 text-center text-white">
                        {formatCurrency(row[`${period.key} 90`])}
                      </td>
                      <td className="py-3 px-3 text-center text-white">
                        {formatCurrency(row[`${period.key} 60`])}
                      </td>
                      <td className="py-3 px-3 text-center text-white">
                        {formatCurrency(row[`${period.key} 30`])}
                      </td>
                      <td className="py-3 px-3 text-center text-white">
                        {formatCurrency(row[`${period.key} 7`])}
                      </td>
                      <td className="py-3 px-3 text-center text-white font-semibold">
                        {formatCurrency(row[`${period.key} Curr`])}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <span className={`text-lg ${trend.color}`}>{trend.arrow}</span>
                          {percentChange != null && (
                            <span className={`text-sm font-medium ${trend.color}`}>
                              {percentChange > 0 ? '+' : ''}{percentChange.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary Stats */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {PERIODS.map((period) => {
              const percentChange = calculateTrendPercent(row, period.key);
              const trend = getTrendArrow(percentChange);

              return (
                <div key={period.key} className="bg-gray-700/50 rounded p-3">
                  <div className="text-gray-400 mb-1">{period.label} Trend</div>
                  <div className={`text-lg font-semibold ${trend.color}`}>
                    {trend.arrow} {percentChange != null ? `${percentChange.toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {row[`${period.key} 90`] != null && row[`${period.key} Curr`] != null
                      ? `${formatCurrency(row[`${period.key} 90`])} \u2192 ${formatCurrency(row[`${period.key} Curr`])}`
                      : 'Insufficient data'
                    }
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expected Growth (current period estimate -> next period estimate) */}
          <div className="mt-4">
            <div className="text-sm font-medium text-gray-300 mb-2">Expected Growth</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {GROWTH_PERIODS.map((gp) => {
                const growth = calculateGrowthPercent(row, gp);
                const trend = getTrendArrow(growth);
                const from = row[`${gp.fromKey} Curr`];
                const to = row[`${gp.toKey} Curr`];

                return (
                  <div key={gp.key} className="bg-gray-700/50 rounded p-3">
                    <div className="text-gray-400 mb-1">{gp.label}</div>
                    <div className={`text-lg font-semibold ${trend.color}`}>
                      {trend.arrow} {formatTrendPercent(growth)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {from != null && to != null
                        ? `${formatCurrency(from)} \u2192 ${formatCurrency(to)}`
                        : 'Insufficient data'
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      <div className="text-sm text-gray-400 text-center">
        Showing detailed EPS estimate timeline for {data.length} {data.length === 1 ? 'stock' : 'stocks'}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* View Toggle */}
      <div className="flex justify-end">
        <div className="flex bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setViewMode('heatmap')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === 'heatmap'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            Heatmap View
          </button>
          <button
            onClick={() => setViewMode('detailed')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === 'detailed'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            Detailed View
          </button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'heatmap' ? renderHeatmapView() : renderDetailedView()}
    </div>
  );
}

export default ResearchEPSTrendsTable;
